import { uiText } from '../../i18n/uiText';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, RefreshCw, Search, UserRound, X } from 'lucide-react';
import PageIntro from '../ui/PageIntro';
import StatusBadge from '../ui/StatusBadge';
import { formatDate, getFullName } from '../../utils/formatters';

const STATUS_FILTERS = [
  ['all', uiText("Все")],
  ['not_started', uiText("Запланированы")],
  ['in_progress', uiText("В работе")],
  ['ready_to_close', uiText("Готовы к закрытию")],
  ['completed', uiText("Завершены")],
];

export default function AuditeeAuditsView({
  audits,
  summary,
  filters,
  pagination,
  loading,
  error,
  updateFilter,
  resetFilters,
  reload,
}) {
  const hasFilters = Boolean(filters.q || (filters.status && filters.status !== 'all'));

  return (
    <div className="workspace-page auditee-portal-page auditee-audits-page">
      <PageIntro
        eyebrow={uiText("Ваша организация")}
        title={uiText("Мои аудиты")}
        description={uiText("Запланированные и завершённые проверки, вопросы аудитора и рабочие документы.")}
        aside={(
          <button type="button" className="btn btn-outline" onClick={reload}>
            <RefreshCw size={15} />{uiText(" Обновить")}</button>
        )}
      />

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="auditee-summary-strip" aria-label={uiText("Сводка по аудитам")}>
        <div><strong>{summary.attention || 0}</strong><span>{uiText("Требуют участия")}</span></div>
        <div><strong>{summary.active || 0}</strong><span>{uiText("В работе")}</span></div>
        <div><strong>{summary.completed_recent || 0}</strong><span>{uiText("Завершены за 7 дней")}</span></div>
      </div>

      <section className="auditee-audits-browser" aria-labelledby="auditee-audits-list-heading">
        <div className="auditee-audits-toolbar">
          <div className="auditee-search-field">
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              value={filters.q || ''}
              onChange={(event) => updateFilter({ q: event.target.value })}
              placeholder={uiText("Название аудита или площадка")}
              aria-label={uiText("Поиск по моим аудитам")}
            />
            {filters.q ? (
              <button type="button" onClick={() => updateFilter({ q: '' })} aria-label={uiText("Очистить поиск")}><X size={15} /></button>
            ) : null}
          </div>
          <div className="auditee-status-filters" aria-label={uiText("Фильтр по статусу")}>
            {STATUS_FILTERS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={(filters.status || 'all') === value ? 'is-active' : ''}
                onClick={() => updateFilter({ status: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="auditee-section-heading auditee-audits-list-heading">
          <div>
            <h2 id="auditee-audits-list-heading">{uiText("Доступные аудиты")}</h2>
            <p>{loading ? uiText("Загружаем данные…") : uiText("Показано {v0} из {v1}", {v0: (audits.length), v1: (pagination.total || 0)})}</p>
          </div>
          {hasFilters ? <button type="button" className="auditee-text-link" onClick={resetFilters}>{uiText("Сбросить фильтры")}</button> : null}
        </div>

        {loading ? (
          <div className="auditee-audit-card-list" aria-label={uiText("Загрузка")}>
            {[0, 1, 2].map((item) => <div key={item} className="auditee-skeleton-card" />)}
          </div>
        ) : audits.length ? (
          <div className="auditee-audit-card-list">
            {audits.map((audit) => (
              <Link key={audit.id} to={`/audits/${audit.id}`} className="auditee-audit-card">
                <div className="auditee-audit-card-top">
                  <StatusBadge status={audit.status} type="audit" />
                  <span className="auditee-audit-number">{uiText("Аудит #")}{audit.id}</span>
                </div>
                <div className="auditee-audit-card-copy">
                  <h3>{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</h3>
                  <p>{audit.site_name || uiText("Площадка не указана")}</p>
                </div>
                <div className="auditee-audit-card-meta">
                  <span><CalendarDays size={15} /> {formatDate(audit.scheduled_date)}</span>
                  <span><UserRound size={15} /> {getFullName(audit.auditor, uiText("Аудитор назначается"))}</span>
                </div>
                <span className="auditee-audit-card-link">{uiText("Открыть аудит ")}<ArrowRight size={15} /></span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="auditee-empty-panel auditee-empty-panel-large">
            <span className="auditee-empty-icon"><CalendarDays size={21} /></span>
            <div>
              <strong>{hasFilters ? uiText("По выбранным условиям ничего не найдено") : uiText("Аудиты пока не назначены")}</strong>
              <p>{hasFilters ? uiText("Очистите поиск или выберите другой статус.") : uiText("Когда аудитор назначит проверку вашей организации, она появится здесь.")}</p>
              {hasFilters ? <button type="button" className="btn btn-outline btn-sm" onClick={resetFilters}>{uiText("Показать все")}</button> : null}
            </div>
          </div>
        )}

        {pagination.total_pages > 1 ? (
          <div className="auditee-pagination">
            <span>{uiText("Страница ")}{pagination.page}{uiText(" из ")}{pagination.total_pages}</span>
            <div>
              <button type="button" className="btn btn-outline btn-sm" disabled={!pagination.has_prev} onClick={() => updateFilter({ page: Math.max(1, pagination.page - 1) })}>{uiText("Назад")}</button>
              <button type="button" className="btn btn-outline btn-sm" disabled={!pagination.has_next} onClick={() => updateFilter({ page: pagination.page + 1 })}>{uiText("Вперёд")}</button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
